'use client';

/**
 * 글을 한국어로 옮겨 보여 주는 칸 — 버튼 하나와 그 아래 결과.
 *
 * 글을 여는 자리(상세 다이얼로그·소재 다이얼로그)가 **함께 쓰는 하나**다.
 *
 * 🚨 번역문은 **텍스트 노드로 그린다** — `dangerouslySetInnerHTML` 을 쓰지 않는다.
 * 모델이 돌려준 문자열이라 태그가 섞여 들어올 수 있고, 여기서 태그로 그리면 그 순간 XSS 가 성립한다.
 * 방어는 두 겹이다: ① `lib/ai` 경계가 평문을 보장하고 ② 이 화면이 텍스트로 그린다.
 */

import type { Theme, SxProps } from '@mui/material/styles';

import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { log } from 'src/utils/logger';

import { RADIUS } from 'src/theme/ui-tokens';
import { translateToKorean, type AiActionResult } from 'src/actions/ai';

import { Iconify } from 'src/components/iconify';

/** 글 한 덩이를 한국어로 — 실패는 `{ success: false, error }` 로 돌려준다(던지면 「번역 중 오류」로 적는다) */
export type PostTranslate = (text: string) => Promise<AiActionResult<string>>;

type Props = {
  /** 제목 — 없으면 본문만 옮긴다 */
  title?: string;
  /** 본문 */
  body?: string;
  /** 이 값이 바뀌면 결과를 버린다 — 다른 글로 갈아 끼웠다는 뜻 */
  resetKey?: string;
  /** 작은 자리(소재 다이얼로그)용 — 여백과 글자를 줄인다 */
  dense?: boolean;
  /** 번역하는 쪽 — 안 주면 AI 번역(`translateToKorean`) */
  translate?: PostTranslate;
  /** 바깥 여백은 부르는 쪽이 준다 — 블록은 제 바깥 여백을 갖지 않는다 */
  sx?: SxProps<Theme>;
};

export default function PostTranslateBlock({ title = '', body = '', resetKey = '', dense = false, translate = translateToKorean, sx }: Props) {
  const [busy, setBusy] = useState(false);
  const [outTitle, setOutTitle] = useState('');
  const [outBody, setOutBody] = useState('');
  const [shown, setShown] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setBusy(false);
    setOutTitle('');
    setOutBody('');
    setShown(false);
    setError('');
  }, [resetKey]);

  const run = useCallback(async () => {
    if (!title && !body) return;
    setBusy(true);
    setError('');
    try {
      const [t, b] = await Promise.all([
        title ? translate(title) : Promise.resolve(null),
        body ? translate(body) : Promise.resolve(null),
      ]);
      const failed = [t, b].filter((r) => r && !r.success);
      if (failed.length > 0) {
        setError(failed.map((r) => (r && !r.success ? r.error : '')).filter(Boolean).join(' · ') || '번역하지 못했습니다.');
        return;
      }
      if (t?.success) setOutTitle(t.data);
      if (b?.success) setOutBody(b.data);
      setShown(true);
    } catch (e) {
      log.error('[PostTranslateBlock] 번역 실패', e);
      setError('번역 중 오류가 발생했습니다.');
    } finally {
      setBusy(false);
    }
  }, [title, body, translate]);

  if (!title && !body) return null;

  return (
    <Box sx={sx}>
      <Button
        variant="outlined"
        size="small"
        startIcon={busy ? <CircularProgress size={16} /> : <Iconify icon="solar:global-linear" width={16} height={16} />}
        onClick={shown ? () => setShown(false) : (outTitle || outBody ? () => setShown(true) : run)}
        disabled={busy}
        sx={{
          borderRadius: RADIUS.lg,
          textTransform: 'none',
          fontSize: dense ? 13 : 14,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'primary.lighter' },
        }}
      >
        {busy ? '번역 중...' : shown ? '번역 접기' : '번역 보기'}
      </Button>

      {error && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'error.lighter', borderRadius: RADIUS.lg, border: '1px solid', borderColor: 'error.light' }}>
          <Typography variant="body2" sx={{ color: 'error.main' }}>{error}</Typography>
        </Box>
      )}

      {shown && (outTitle || outBody) && (
        <Box sx={{ mt: 1.5, p: dense ? 1.5 : 2, bgcolor: 'grey.50', borderRadius: RADIUS.lg, border: '1px solid', borderColor: 'divider' }}>
          {outTitle && (
            <Typography
              variant={dense ? 'subtitle2' : 'h6'}
              sx={{ color: 'text.primary', mb: outBody ? 1 : 0, whiteSpace: 'pre-line', wordBreak: 'break-word' }}
            >
              {outTitle}
            </Typography>
          )}
          {outBody && (
            <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {outBody}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
